import { isNil } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import SidebarButton from "../../components/sidebar/SidebarButton";
import SidebarGroup from "../../components/sidebar/SidebarGroup";
import SidebarPopoverButton from "../../components/sidebar/SidebarPopoverButton";
import SidebarPopoverList from "../../components/sidebar/SidebarPopoverList";
import SidebarView from "../../components/sidebar/SidebarView";
import {
  CONSTRAINT_PANEL,
  ENTITY_LIST_PANEL,
  GLOBAL_FILTER_ID,
  GLOBAL_FILTER_PANEL,
  HARVARD_SETTINGS_PANEL,
  LOCAL_FILTER_PANEL,
  MARKER_LIST_PANEL,
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  RELATIONSHIP_VIEW,
  SCROLL_TO_DATE_PANEL,
  SEARCH_PANEL,
  SETTINGS_PANEL,
  SUBWAY_VIEW,
  SYNC_BINDER_PANEL,
  TIMELINE_VIEW,
  TUTORIAL_PANEL
} from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions, { getFontSizeInRangeLimit, scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { typeSelectors } from "../../modules/definition/types";
import { filterSelectors } from "../../modules/filters";
import { itemSelectors } from "../../modules/items";
import { preferencesSelectors } from "../../modules/preferences";
import { syncSelectors } from "../../modules/sync";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import { uiStateSelectors } from "../../modules/uiState";
import { tutorialDisplaySelectors } from "../../tutorial/selectors";
import { PropTypeShapes } from "../../types";
import { chooseObjectForReadonly, mergeDispatchToProps, wrapEditingComponent } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { trackEvent } from "../../utils/trackAnalytics";
import userAgent from "../../utils/userAgent";
import {
  MAC_KEY_LABEL_COMMAND,
  MAC_KEY_LABEL_OPTION,
  MAC_KEY_LABEL_SHIFT,
  WINDOWS_KEY_LABEL_ALTERNATIVE,
  WINDOWS_KEY_LABEL_CONTROL,
  WINDOWS_KEY_LABEL_SHIFT
} from "../appMenu/constants";
import constraintPanelSelectors from "../constraintPanel/selectors";
import syncBinderSelectors from "../syncBinder/selectors";
import TutorialPopoverLabel from "../tutorialPanel/TutorialPopoverLabel";
import selectors from "./selectors";

const getHeightFromRef = ref => {
  if (ref && ref.current) {
    return ref.current.getBoundingClientRect().height;
  }
  return 0;
};

const isMac = userAgent.isMacApp();
const isIos = preprocessorQuery.isIos();

const functionKeys = {
  command: isMac ? MAC_KEY_LABEL_COMMAND : WINDOWS_KEY_LABEL_CONTROL,
  shift: isMac ? MAC_KEY_LABEL_SHIFT : WINDOWS_KEY_LABEL_SHIFT,
  option: isMac ? MAC_KEY_LABEL_OPTION : WINDOWS_KEY_LABEL_ALTERNATIVE
};

const getAddItemShortCut = count => {
  const { command, shift, option } = functionKeys;
  const strArr =
    count === 0 ? [command, "N"] : isMac ? [option, shift, command, count] : [command, option, shift, count];
  return isMac ? strArr.join("") : strArr.join("+");
};

const getAddMarkerShortCut = () => {
  const { command, option } = functionKeys;
  const key = "M";
  const strArr = isMac ? [option, command, key] : [command, option, key];
  return isMac ? strArr.join("") : strArr.join("+");
};

const addMarkerShortCut = getAddMarkerShortCut();

const typeOptions = types => {
  return [
    types
      .map((t, i) => {
        return {
          id: t.value,
          label: t.label,
          icon: t.icon,
          color: t.color,
          shortcut: isIos ? null : getAddItemShortCut(i),
          disabled: t.disabled
        };
      })
      .toJS(),
    //#!if mobile !== true
    [
      {
        id: "marker",
        label: "Calendar Marker",
        icon: "pin",
        color: "lightBlue",
        shortcut: isIos ? null : addMarkerShortCut,
        disabled: false
      }
    ]
    //#!endif
  ];
};

const renderTypeButtons = function(
  types,
  visibleTypeList,
  pulsateIconId,
  onTypeClick,
  itemIdsByType,
  availableHeight,
  fontSize,
  leftPanelMode
) {
  const typesCount = types.size;
  const maxNumIcons = isNil(availableHeight)
    ? typesCount
    : Math.floor(availableHeight / scalePixelsToFontSize(40, fontSize));
  const diff = typesCount - maxNumIcons;

  const mapTypeToButton = t => {
    //#!if readonly === true
    if (!itemIdsByType.get(t.id).size) {
      return null;
    }
    //#!endif
    return (
      <SidebarButton
        key={t.id}
        pulse={t.id === pulsateIconId}
        icon={t.icon}
        color={t.color}
        onClick={onTypeClick}
        active={t.id === visibleTypeList && leftPanelMode === ENTITY_LIST_PANEL}
        clickContext={t.id}
        tooltip={`Toggle ${t.pluralLabel} Panel`}
      />
    );
  };

  const showingTypes = types
    .slice(0, diff > 0 ? maxNumIcons - 1 : maxNumIcons)
    .map(mapTypeToButton)
    .toArray();

  const activeTypeIndex = types.findIndex(t => t.id === visibleTypeList);
  const activeType = types.get(activeTypeIndex);
  const activeInGroup = activeTypeIndex >= maxNumIcons - 1;

  const foldedTypes =
    diff > 0
      ? [
        <SidebarPopoverButton
          key="sidebarMore"
          id="sidebarMore"
          icon="ellipsis"
          color={activeInGroup ? activeType.color : null}
          isActive={activeInGroup}
          tooltip="Show more options"
          onClick={onTypeClick}
          placement="right"
        >
          {types
            .slice(maxNumIcons - 1)
            .map(mapTypeToButton)
            .toArray()}
        </SidebarPopoverButton>
      ]
      : [];

  return showingTypes.concat(foldedTypes);
};

const onAddMenuItemClick = (props, typeId) => {
  if (typeId === "marker") {
    PubSub.publish(PubSubTopics.createMarker);
    // props.onAddMarkerClick();

    trackEvent("createCalendarMarkerFromSidebarPopover", "createCalendarMarker");
  } else if (typeId === "bookmark") {
    props.onAddBookmarkClick();

    trackEvent("createBookmarkFromSidebarPopover", "createBookmark");
  } else {
    // Otherwise, the type is just something we want to signal to the active view
    PubSub.publish(PubSubTopics.createItem, { typeId });

    trackEvent("createItemFromSidebarPopover", "createItem", typeId);
  }
};

class Sidebar extends PureComponent {
  constructor(props) {
    super(props);

    this._topRef = React.createRef();
    this._syncRef = React.createRef();
    this._constraintRef = React.createRef();
    this._bookmarkRef = React.createRef();
    this._bottomRef = React.createRef();
  }

  getMiddleGroupAvailableHeight() {
    const refsHeight =
      getHeightFromRef(this._topRef) +
      getHeightFromRef(this._syncRef) +
      getHeightFromRef(this._constraintRef) +
      getHeightFromRef(this._bookmarkRef) +
      getHeightFromRef(this._bottomRef);

    return this.props.containerRect.height - refsHeight;
  }

  render() {
    const {
      addTypeOptions,
      pulsateIconId,
      leftPanelMode,
      onSearchClick,
      globalFilterIsActive,
      localFilterIsActive,
      onFilterClick,
      syncChangeCount,
      syncFileMissing,
      syncConfigRequired,
      onSyncClick,
      onMarkerClick,
      types,
      visibleTypeList,
      onTypeClick,
      itemIdsByType,
      warningCount,
      tutorialIsActive,
      onTutorialClick,
      onSettingsClick,
      globalFontSize,
      activeViewType,
      onScrollToDateClick
    } = this.props;
    //#!if harvard === true
    const { onHarvardSettingsClick } = this.props;
    //#!endif
    const typeOpts = typeOptions(addTypeOptions);
    const paddingLeft = this.props.paddingLeft;
    const width = Dimensions.SIDEBAR_WIDTH + paddingLeft;
    const fontSize = getFontSizeInRangeLimit(globalFontSize);

    const isIos = preprocessorQuery.isIos();
    const isTablet = preprocessorQuery.isTablet();
    const isMobile = preprocessorQuery.isMobile();
    const isReadonly = preprocessorQuery.isReadonly();
    const isHarvard = preprocessorQuery.isHarvard();

    const onAddClick = typeId => onAddMenuItemClick(this.props, typeId);
    const showLayoutSettings =
      activeViewType === TIMELINE_VIEW ||
      activeViewType === RELATIONSHIP_VIEW ||
      activeViewType === SUBWAY_VIEW ||
      activeViewType === NARRATIVE_VIEW ||
      activeViewType === MINDMAP_VIEW;

    const topGroup = isMobile ? null : (
      <SidebarGroup position="top" border={true} ref={this._topRef}>
        {wrapEditingComponent(
          <SidebarPopoverButton
            key="sidebarAddButton"
            id="sidebarAddButton"
            icon="sidebarAdd"
            color="iconSidebar"
            isActive={false}
            tooltip="Add Item"
            onClick={onAddClick}
            placement="right-start"
          >
            <SidebarPopoverList options={typeOpts} onClick={onAddClick} />
          </SidebarPopoverButton>
        )}
        <Fragment>
          <SidebarButton
            id="sidebarSearchButton"
            icon="toolbarSearch"
            active={leftPanelMode === SEARCH_PANEL}
            color="iconSidebar"
            onClick={onSearchClick}
            pulse={pulsateIconId === interfaceIdentifiers.SEARCH}
            tooltip="Toggle Search Panel"
          />
          <SidebarButton
            id="sidebarFilterButton"
            icon={globalFilterIsActive || localFilterIsActive ? "toolbarFilterActive" : "toolbarFilter"}
            active={leftPanelMode === GLOBAL_FILTER_PANEL || leftPanelMode === LOCAL_FILTER_PANEL}
            color="iconSidebar"
            onClick={onFilterClick}
            pulse={pulsateIconId === interfaceIdentifiers.FILTER}
            tooltip="Toggle Filter Panel"
          />
        </Fragment>
        {isTablet ? (
          <SidebarButton
            id="sidebarScrollToDate"
            icon="sidebarScrollToDate"
            active={leftPanelMode === SCROLL_TO_DATE_PANEL}
            color="iconSidebar"
            onClick={onScrollToDateClick}
            tooltip="Scroll to Date"
          />
        ) : null}
      </SidebarGroup>
    );

    let component = (
      <SidebarView width={width} fontSize={fontSize}>
        {topGroup}
        <SidebarGroup position="top" border={true} ref={this._bookmarkRef}>
          {!isIos
            ? wrapEditingComponent(
              <Fragment>
                <SidebarButton
                  icon={
                    syncChangeCount > 0 || syncFileMissing || syncConfigRequired ? "sidebarSyncActive" : "sidebarSync"
                  }
                  active={leftPanelMode === SYNC_BINDER_PANEL}
                  color="iconSidebar"
                  onClick={onSyncClick}
                  pulse={pulsateIconId === interfaceIdentifiers.SYNC}
                  tooltip="Toggle Sync Panel"
                />
                <SidebarButton
                  id="sidebarConstraintButton"
                  icon="inspectorConstraints"
                  active={this.props.leftPanelMode === CONSTRAINT_PANEL}
                  color="iconSidebar"
                  onClick={this.props.onConstraintClick}
                  badge={warningCount}
                  badgeColor="statusError"
                  tooltip="Toggle Constraints Panel"
                  pulse={pulsateIconId === interfaceIdentifiers.CONSTRAINTS}
                />
              </Fragment>
            )
            : null}
          {!isReadonly && !isMobile
            ? wrapEditingComponent(
              <SidebarButton
                icon="pin"
                active={leftPanelMode === MARKER_LIST_PANEL}
                color="iconSidebar"
                onClick={onMarkerClick}
                pulse={pulsateIconId === interfaceIdentifiers.MARKER}
                tooltip="Toggle Calendar Markers Panel"
              />
            )
            : null}
        </SidebarGroup>
        <SidebarGroup position="middle">
          {renderTypeButtons(
            types,
            visibleTypeList,
            pulsateIconId,
            onTypeClick,
            itemIdsByType,
            this.getMiddleGroupAvailableHeight(),
            fontSize,
            leftPanelMode
          )}
        </SidebarGroup>
        {isIos ? (
          <SidebarGroup position="bottom" ref={this._bottomRef}>
            {isTablet ? (
              <SidebarButton
                id="sidebarLayoutSettings"
                icon={showLayoutSettings ? "footerCardDetails" : ""}
                active={leftPanelMode === SETTINGS_PANEL}
                color="iconSidebar"
                onClick={onSettingsClick}
                tooltip="Toggle Layout Settings Panel"
                pulse={pulsateIconId === interfaceIdentifiers.IOS_VIEW_DETAILS}
              />
            ) : null}
            {wrapEditingComponent(
              <Fragment>
                <SidebarButton
                  icon={tutorialIsActive ? "toolbarTutorialActive" : "toolbarTutorial"}
                  color="iconSidebar"
                  active={leftPanelMode === TUTORIAL_PANEL}
                  pulse={pulsateIconId === interfaceIdentifiers.TUTORIALS}
                  onClick={onTutorialClick}
                  tooltip="Toggle Tutorial Panel"
                  id="tutorialButton"
                />
                <TutorialPopoverLabel target="tutorialButton" popoverPlacement="left-start" />
              </Fragment>
            )}
          </SidebarGroup>
        ) : null}
      </SidebarView>
    );

    if (isHarvard) {
      component = (
        <SidebarView width={width} fontSize={fontSize}>
          <SidebarGroup position="middle" border={false}>
            <SidebarButton
              id="sidebarSearchButton"
              icon="toolbarSearch"
              active={leftPanelMode === SEARCH_PANEL}
              color="iconSidebar"
              onClick={onSearchClick}
              pulse={pulsateIconId === interfaceIdentifiers.SEARCH}
              tooltip="Toggle Search Panel"
            />
            {!this.props.disableFiltering ? (
              <SidebarButton
                id="sidebarFilterButton"
                icon={localFilterIsActive ? "toolbarFilterActive" : "toolbarFilter"}
                active={leftPanelMode === LOCAL_FILTER_PANEL}
                color="iconSidebar"
                onClick={onFilterClick}
                pulse={pulsateIconId === interfaceIdentifiers.FILTER}
                tooltip="Toggle Filter Panel"
              />
            ) : null}
            <SidebarButton
              id="sidebarScrollToDate"
              icon="sidebarScrollToDate"
              active={leftPanelMode === SCROLL_TO_DATE_PANEL}
              color="iconSidebar"
              onClick={onScrollToDateClick}
              tooltip="Scroll to Date"
            />
            <SidebarButton
              id="sidebarSettingsButton"
              icon="sidebarSettings"
              active={leftPanelMode === HARVARD_SETTINGS_PANEL}
              color="iconSidebar"
              onClick={onHarvardSettingsClick}
              tooltip="Toggle Settings Panel"
            />
          </SidebarGroup>
        </SidebarView>
      );
    }

    return component;
  }
}

Sidebar.propTypes = {
  types: PropTypeShapes.itemTypeList.isRequired,
  leftPanelMode: PropTypes.string,
  addTypeOptions: PropTypeShapes.listOptionList,
  visibleTypeList: PropTypes.string,
  globalFilterIsActive: PropTypes.bool,
  localFilterIsActive: PropTypes.bool,
  syncChangeCount: PropTypes.number,
  syncFileMissing: PropTypes.bool,
  syncConfigRequired: PropTypes.bool,
  activeViewId: PropTypes.string.isRequired,
  activeViewType: PropTypes.string,
  pulsateIconId: PropTypes.string,
  warningCount: PropTypes.number,
  tutorialIsActive: PropTypes.bool,
  onAddBookmarkClick: PropTypes.func.isRequired,
  onTypeClick: PropTypes.func.isRequired,
  onSyncClick: PropTypes.func,
  onBookmarkClick: PropTypes.func.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  onSearchClick: PropTypes.func.isRequired,
  onFilterClick: PropTypes.func.isRequired,
  onTutorialClick: PropTypes.func,
  globalFontSize: PropTypes.number.isRequired,
  // #!if tablet === true || harvard === true
  onScrollToDateClick: PropTypes.func.isRequired,
  // #!endif
  // #!if readonly === true
  itemIdsByType: ImmutablePropTypes.map,
  // #!endif
  // #!if tablet === true
// onSettingsClick: PropTypes.func.isRequired,
  // #!endif
  // #!if harvard === true
  onHarvardSettingsClick: PropTypes.func.isRequired,
  disableFiltering: PropTypes.bool.isRequired,
  // #!endif
  onConstraintClick: PropTypes.func.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number.isRequired
};

const mapStateToProps = state => {
  const activeViewId = appStateSelectors.getActiveViewId(state.present);

  return {
    types: chooseObjectForReadonly(
      itemSelectors.getSidebarTypesHasItem(state.present),
      typeSelectors.getSidebarTypes(state.present)
    ),
    visibleTypeList: appStateSelectors.getVisibleTypeListId(state.present),
    leftPanelMode: appStateSelectors.getLeftPanelMode(state.present),
    addTypeOptions: selectors.getTypeOptions(state.present),
    globalFilterIsActive: filterSelectors.isFilterWithIdActive(state.present, GLOBAL_FILTER_ID),
    localFilterIsActive: filterSelectors.isFilterWithIdActive(state.present, activeViewId),
    activeViewId: activeViewId,
    activeViewType: appStateSelectors.getActiveViewType(state.present),
    pulsateIconId: uiStateSelectors.getPulsateIconId(state.present),
    syncChangeCount: syncBinderSelectors.getBinderChangeCount(state.present),
    syncFileMissing: syncSelectors.getSyncFileMissing(state.present),
    syncConfigRequired: syncSettingsSelectors.isFurtherConfigRequired(state.present),
    warningCount: constraintPanelSelectors.getWarningsCount(state.present),
    globalFontSize: preferencesSelectors.getGlobalFontSize(state.present),
    // #!if readonly === true
    itemIdsByType: itemSelectors.getItemIdsByType(state.present),
    // #!endif
    // #!if iOS === true && readonly !== true
// tutorialIsActive: tutorialDisplaySelectors.tutorialIsActive(state.present),
    // #!endif
    // #!if harvard === true
    disableFiltering: appStateSelectors.getDisableFiltering(state.present)
    // #!endif
  };
};

const readonlyActions = {
  onTypeClick: appStateOperations.toggleEntityList,
  onSearchClick: appStateOperations.toggleSearchPanel,
  onFilterClick: appStateOperations.toggleFilterPanel,
  //#!if tablet === true || harvard === true
  onScrollToDateClick: appStateOperations.toggleScrollToDatePanel,
  //#!endif
  //#!if iOS === true
// onTutorialClick: appStateOperations.toggleTutorial,
  //#!endif
  // #!if tablet === true
// onSettingsClick: appStateOperations.toggleSettingsPanel,
  // #!endif
  // #!if harvard === true
  onHarvardSettingsClick: appStateOperations.toggleHarvardSettingsPanel
  // #!endif
};

const editingActions = {
  onAddBookmarkClick: () => {
    // TODO: Add bookmarks panel
  },
  onBookmarkClick: () => {
    // TODO: Add bookmarks panel
  },
  onMarkerClick: appStateOperations.toggleMarkerPanel,
  //#!if iOS !== true
  onSyncClick: appStateOperations.toggleSyncBinderPanel,
  //#!endif
  onConstraintClick: appStateOperations.toggleConstraintPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withContainerContext(Sidebar));
